import React from 'react';
import { Helmet } from 'react-helmet';
import PageHero from '../components/PageHero';
import SectionTitle from '../components/SectionTitle';
import Reveal from '../components/Reveal';
import ContactCTA from '../components/ContactCTA';
import { company, greeting } from '../data/siteData';

const CompanyInfo = () => {
  const overview = [
    ['会社名', company.name],
    ['設立年月', company.established],
    ['所在地', company.address],
    ['電話 / FAX', company.phone],
    ['メールアドレス', company.email],
    ['代表取締役', company.representative],
  ];

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: company.name,
    url: 'https://www.ryukk.jp',
    logo: 'https://www.ryukk.jp/logo.png',
    foundingDate: '1947-10',
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'JP',
      postalCode: company.postalCode,
      addressRegion: '埼玉県',
      addressLocality: '児玉郡神川町',
      streetAddress: '原新田28-1',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+81-495-71-5696',
      contactType: 'customer support',
      availableLanguage: ['Japanese', 'Chinese'],
    },
    sameAs: company.marketplace.map((shop) => shop.href),
  };

  return (
    <>
      <Helmet>
        <title>会社情報 | RYU株式会社</title>
        <meta
          name="description"
          content="RYU株式会社の会社概要、企業理念、代表挨拶、アクセス情報をご案内します。1947年の設立以来、酒類卸売・流通・EC事業を展開しています。"
        />
        <meta property="og:title" content="会社情報 | RYU株式会社" />
        <meta property="og:description" content="信用を、企業の基準に。RYU株式会社の会社情報です。" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.ryukk.jp/company" />
        <meta property="og:image" content="https://www.ryukk.jp/ogp.jpg" />
        <link rel="canonical" href="https://www.ryukk.jp/company" />
        <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      </Helmet>

      <PageHero
        label="COMPANY"
        title="信用を、企業の基準に。"
        description="会社の歩みと、私たちが大切にすること。"
      />

      <nav className="page-anchor" aria-label="会社情報ページ内ナビゲーション">
        <div className="container">
          <a href="#outline">会社概要</a>
          <a href="#philosophy">企業理念</a>
          <a href="#message">代表挨拶</a>
          <a href="#access">アクセス</a>
        </div>
      </nav>

      <section id="outline" className="company-outline section">
        <div className="container company-outline__grid">
          <SectionTitle label="CORPORATE PROFILE" title="会社概要" />
          <Reveal as="div" className="profile-table" delay={100}>
            <dl>
              {overview.map(([label, value]) => (
                <div key={label}>
                  <dt>{label}</dt>
                  <dd>{value}</dd>
                </div>
              ))}
              <div>
                <dt>事業内容</dt>
                <dd>
                  <ul>{company.businesses.map((item) => <li key={item}>{item}</li>)}</ul>
                </dd>
              </div>
              <div>
                <dt>取引銀行</dt>
                <dd className="profile-table__banks">
                  {company.banks.map((bank) => <span key={bank}>{bank}</span>)}
                </dd>
              </div>
            </dl>
          </Reveal>
        </div>
      </section>

      <section id="philosophy" className="philosophy section">
        <div className="container philosophy__grid">
          <Reveal className="philosophy__visual">
            <p className="eyebrow">OUR PRINCIPLE</p>
            <p className="principle-word">信用を、<br />積み重ねる。</p>
            <span>RYU CORPORATION</span>
          </Reveal>
          <div className="philosophy__copy">
            <SectionTitle label="PHILOSOPHY" title={<>正しい流通が、<br />価値を守る。</>} dark />
            <Reveal as="p" delay={120}>
              RYU株式会社は、酒類流通のプロフェッショナルとして、流通の信頼性・透明性・迅速性を徹底し、
              国内外のビジネスパートナーと継続的な信頼関係を築いています。
            </Reveal>
            <Reveal as="p" delay={180}>
              正規流通ルートと物流インフラを活用し、多様な業態のニーズに対応した供給体制を整えています。
              一本の価値を損なわず、次の担い手へつなぐことが私たちの使命です。
            </Reveal>
          </div>
        </div>
      </section>

      <section id="message" className="message section">
        <div className="container message__grid">
          <SectionTitle label="MESSAGE" title="代表挨拶" />
          <Reveal className="message__body" delay={100}>
            <p className="message__lead">信頼は、日々の「当然」を<br />守り続けることから生まれる。</p>
            {greeting.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
            <p className="message__signature">
              <span>代表取締役</span>
              {company.representative}
            </p>
          </Reveal>
        </div>
      </section>

      <section id="access" className="access section">
        <div className="container">
          <SectionTitle label="ACCESS" title="アクセス" />
          <div className="access__grid">
            <Reveal>
              <p>〒{company.postalCode}<br />{company.address}</p>
              {company.access.map((line) => <p key={line}>{line}</p>)}
              <a href={`tel:${company.phone.replaceAll('-', '')}`}>{company.phone}</a>
            </Reveal>
            <Reveal className="access__map" delay={100}>
              <iframe
                title="RYU株式会社 地図"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3218.8992503057366!2d139.1202061!3d36.2176437!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x601ec1f82feafa51%3A0xb413493886a4badd!2s28-1%20Harashinden%2C%20Kamikawa%2C%20Kodama%20District%2C%20Saitama%20367-0242!5e0!3m2!1sja!2sjp!4v1745474336793!5m2!1sja!2sjp"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </Reveal>
          </div>
        </div>
      </section>

      <ContactCTA />
    </>
  );
};

export default CompanyInfo;
